(() => {
  if (customElements.get('wolf-payment-terms')) return;

  class WolfPaymentTerms extends HTMLElement {
    async connectedCallback() {
      if (!this.dataset.logoUrl) return;
      const connection = this.connection = {};
      await customElements.whenDefined('shopify-payment-terms');
      if (!this.isConnected || this.connection !== connection) return;
      this.observer = new MutationObserver(() => this.watchTerms());
      this.observer.observe(this, { childList: true, subtree: true });
      this.watchTerms();
    }

    disconnectedCallback() {
      this.connection = null;
      this.observer?.disconnect();
      this.termsObserver?.disconnect();
      clearTimeout(this.rootRetry);
      this.clearTransition();
      this.terms = null;
      this.termsRoot = null;
    }

    replaceTerms(content) {
      const compact = this.termsRoot?.querySelector('[data-wolf-compact]');
      const preserve = Boolean(compact || this.placeholder);
      const bounds = this.getBoundingClientRect();
      this.clearTransition();
      this.termsObserver?.disconnect();
      clearTimeout(this.rootRetry);
      this.terms = null;
      this.termsRoot = null;

      if (preserve) {
        this.style.setProperty('--payment-width', `${bounds.width}px`);
        this.style.setProperty('--payment-height', `${bounds.height}px`);
        this.setAttribute('data-payment-loading', '');
        this.setAttribute('aria-busy', 'true');
        content.querySelector('form').inert = true;
      }
      this.replaceChildren(content);

      if (preserve) {
        const placeholder = document.createElement('span');
        placeholder.className = 'wolf-payment-terms__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        const logo = document.createElement('img');
        logo.src = this.dataset.logoUrl;
        logo.alt = '';
        const label = document.createElement('span');
        label.textContent = 'Learn more';
        placeholder.append(logo, label);
        this.placeholder = placeholder;
        this.append(placeholder);
        // Restore the native fallback if Shopify no longer renders an offer.
        this.readyTimeout = setTimeout(() => this.revealTerms(), 8000);
      }
      this.rootAttempts = 0;
      this.watchTerms();
    }

    clearTransition() {
      clearTimeout(this.readyTimeout);
      this.paymentAnimations?.forEach((animation) => animation.cancel());
      this.paymentAnimations = [];
      this.placeholder?.remove();
      this.placeholder = null;
      this.removeAttribute('data-payment-loading');
      this.removeAttribute('aria-busy');
      this.style.removeProperty('--payment-width');
      this.style.removeProperty('--payment-height');
      const form = this.querySelector('form');
      if (form) form.inert = false;
    }

    revealTerms() {
      if (!this.hasAttribute('data-payment-loading')) return;
      clearTimeout(this.readyTimeout);
      const form = this.querySelector('form');
      const placeholder = this.placeholder;
      this.removeAttribute('data-payment-loading');
      this.removeAttribute('aria-busy');
      this.style.removeProperty('--payment-width');
      this.style.removeProperty('--payment-height');
      if (form) form.inert = false;

      if (!form?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        placeholder?.remove();
        this.placeholder = null;
        return;
      }
      const incoming = form.animate([
        { opacity: 0, transform: 'translateY(2px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ], { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      const outgoing = placeholder?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-out', fill: 'forwards' });
      this.paymentAnimations = [incoming, outgoing].filter(Boolean);
      const removePlaceholder = () => {
        placeholder?.remove();
        if (this.placeholder === placeholder) this.placeholder = null;
      };
      if (outgoing) outgoing.finished.then(removePlaceholder, removePlaceholder);
      else removePlaceholder();
    }

    watchTerms() {
      const terms = this.querySelector('shopify-payment-terms');
      const root = terms?.shadowRoot;
      if (terms === this.terms && root && root === this.termsRoot) return;
      this.termsObserver?.disconnect();
      this.terms = terms;
      this.termsRoot = root;
      clearTimeout(this.rootRetry);
      if (!root) {
        // Some Shopify upgrades attach their root after the element connects.
        if (terms && (this.rootAttempts = (this.rootAttempts || 0) + 1) <= 80) {
          this.rootRetry = setTimeout(() => {
            if (this.isConnected && this.terms === terms) {
              this.termsRoot = null;
              this.watchTerms();
            }
          }, 100);
        }
        return;
      }
      this.rootAttempts = 0;
      this.termsObserver = new MutationObserver(() => this.compact(root));
      this.termsObserver.observe(root, { childList: true, subtree: true });
      this.compact(root);
    }

    compact(root) {
      if (root !== this.termsRoot) return;
      const banner = root.querySelector('#shopify-installments');
      const content = banner?.querySelector('#shopify-installments-content');
      const button = banner?.querySelector('button#shopify-installments-cta[aria-haspopup="dialog"]');
      const row = content?.parentElement;
      // Shopify has no public compact option. Keep its real dialog button and
      // fall back to the native banner if this narrowly checked markup changes.
      // Shopify currently uses the paragraph itself as the banner; older
      // storefronts use a wrapper with one paragraph. Both keep the same CTA.
      const supported = row?.tagName === 'P' && button?.parentElement === row &&
        (row === banner || row.parentElement === banner);
      banner?.toggleAttribute('data-wolf-compact', Boolean(supported));
      if (!supported) return;
      row.setAttribute('data-wolf-payment-row', '');
      if (root.querySelector('style[data-wolf-payment-style]')) {
        this.revealTerms();
        return;
      }

      const style = document.createElement('style');
      style.dataset.wolfPaymentStyle = '';
      style.textContent = `
        #shopify-installments[data-wolf-compact] { font-family: inherit; }
        #shopify-installments[data-wolf-compact][data-wolf-payment-row],
        #shopify-installments[data-wolf-compact] > [data-wolf-payment-row] {
          display: inline-flex; align-items: center; align-self: flex-start;
          gap: 8px; margin: 0; white-space: nowrap;
        }
        #shopify-installments[data-wolf-compact][data-wolf-payment-row]::before,
        #shopify-installments[data-wolf-compact] > [data-wolf-payment-row]::before {
          content: ''; display: block; width: 56px; height: 14px; flex: 0 0 56px;
          background: url(${JSON.stringify(this.dataset.logoUrl)}) center / contain no-repeat;
        }
        #shopify-installments[data-wolf-compact] #shopify-installments-content { display: none; }
        #shopify-installments[data-wolf-compact] #shopify-installments-cta {
          display: inline-flex; align-items: center; min-height: 44px; padding: 0;
          font-family: inherit; font-size: 12px; font-weight: 400; line-height: 20px;
          color: #2f2f2f; border: 0; background: transparent; cursor: pointer;
        }
        #shopify-installments[data-wolf-compact] #shopify-installments-cta > * { display: none; }
        #shopify-installments[data-wolf-compact] #shopify-installments-cta::after {
          content: 'Learn more'; text-decoration: underline; text-underline-offset: 2px;
        }
        #shopify-installments[data-wolf-compact] #shopify-installments-cta:focus-visible {
          outline: 2px solid currentColor; outline-offset: 3px; border-radius: 2px;
        }
      `;
      root.append(style);
      this.revealTerms();
    }
  }

  customElements.define('wolf-payment-terms', WolfPaymentTerms);
})();
